import { Button } from '../ui/button'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'

const flowingPrompts = {
  zh: [
    '帮我整理这份季度销售报告的核心数据',
    '查询公司差旅报销的具体流程',
    '本店招牌菜的食材用量标准是多少',
    '汇总客户反馈中的高频问题',
    '梳理新员工入职需要完成的流程',
    '本月门店巡检有哪些不合格项',
  ],
  en: [
    'Summarize core data from the quarterly sales report',
    'Look up the travel reimbursement process',
    'Ingredient standards for our signature dish',
    'Compile frequent customer feedback issues',
    'List onboarding steps for new employees',
    'Which items failed this month\'s inspection',
  ],
}

interface HeroSectionProps {
  isLoggedIn: boolean
  onStart: () => void
}

export function HeroSection({ isLoggedIn, onStart }: HeroSectionProps) {
  const { lang, t } = useI18n()
  const prompts = lang === 'zh' ? flowingPrompts.zh : flowingPrompts.en
  const [promptIndex, setPromptIndex] = useState(0)
  const [displayText, setDisplayText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  useEffect(() => {
    setPromptIndex(0)
    setDisplayText('')
    setIsDeleting(false)
  }, [lang])

  useEffect(() => {
    const currentPrompt = prompts[promptIndex]
    let timeout: ReturnType<typeof setTimeout>

    if (!isDeleting && displayText === currentPrompt) {
      timeout = setTimeout(() => setIsDeleting(true), 2200)
    } else if (isDeleting && displayText === '') {
      setIsDeleting(false)
      setPromptIndex((prev) => (prev + 1) % prompts.length)
    } else if (isDeleting) {
      timeout = setTimeout(() => setDisplayText(currentPrompt.substring(0, displayText.length - 1)), 25)
    } else {
      timeout = setTimeout(() => setDisplayText(currentPrompt.substring(0, displayText.length + 1)), 55)
    }
    return () => clearTimeout(timeout)
  }, [displayText, isDeleting, promptIndex, prompts])

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ scrollSnapAlign: 'start' }}>
      {/* Artistic gradient mesh */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] rounded-full opacity-70"
          style={{ background: 'radial-gradient(circle, rgba(199,210,254,0.5) 0%, transparent 70%)', animation: 'drift1 25s ease-in-out infinite alternate' }} />
        <div className="absolute -bottom-[30%] -right-[20%] w-[70%] h-[70%] rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle, rgba(254,215,170,0.45) 0%, transparent 70%)', animation: 'drift2 20s ease-in-out infinite alternate' }} />
        <div className="absolute top-[20%] right-[10%] w-[40%] h-[40%] rounded-full opacity-50"
          style={{ background: 'radial-gradient(circle, rgba(196,181,253,0.4) 0%, transparent 70%)', animation: 'drift3 22s ease-in-out infinite alternate' }} />
      </div>

      <div className="relative z-10 w-full px-6 sm:px-10 max-w-[960px] mx-auto">
        {/* Badge */}
        <div className={`text-center mb-16 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/[0.06] bg-white/60 backdrop-blur-sm text-sm text-gray-400">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
            </span>
            PrimeBook AI
          </span>
        </div>

        {/* Title */}
        <div className={`text-center mb-14 transition-all duration-1000 delay-100 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          <h1
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-[2.8rem] tracking-tight leading-[1.4] transition-all duration-1000 delay-300 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-gray-400 font-normal">{t('让每个企业拥有', 'Every enterprise deserves')}</span>
            <span className="bg-gradient-to-r from-[#1a1a1a] via-[#444] to-[#1a1a1a] bg-clip-text text-transparent font-semibold"> {t('专属AI管家', 'a dedicated AI Butler')}</span>
          </h1>
        </div>

        {/* Subtitle */}
        <p className={`text-center text-base md:text-lg text-gray-400 max-w-lg mx-auto mb-16 leading-relaxed transition-all duration-700 delay-500 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          {t('上传企业文档，即刻构建专属知识库', 'Upload documents, build your knowledge base instantly')}
          <br />
          {t('AI管家深度理解业务，7x24为企业答疑解惑、梳理流程', 'AI Butler deeply understands your business, answering questions 24/7')}
        </p>

        {/* Prompt bar */}
        <div className={`mx-auto max-w-lg mb-12 transition-all duration-700 delay-[600ms] ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div
            className="flex items-center gap-3 px-5 py-4 rounded-2xl border border-black/[0.06] bg-white/70 backdrop-blur-md cursor-pointer hover:border-black/[0.12] hover:bg-white/90 transition-all duration-300 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            onClick={onStart}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 opacity-30">
              <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M11 11L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span className="text-gray-400 text-sm truncate flex-1 text-left">
              {displayText}
              <span className="animate-pulse text-gray-300 ml-0.5">|</span>
            </span>
            <ArrowUpRight className="h-4 w-4 text-gray-300 shrink-0" />
          </div>
        </div>

        {/* CTA */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-[700ms] ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Button size="lg" className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl shadow-sm" onClick={onStart}>
            {isLoggedIn ? t('进入工作台', 'Dashboard') : t('免费开始使用', 'Get Started Free')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" className="border-black/[0.08] bg-white/60 backdrop-blur-sm text-gray-500 hover:bg-white hover:text-gray-900 px-8 h-12 text-sm font-medium rounded-xl"
            onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}>
            {t('了解更多', 'Learn More')}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes drift1 { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(8%,6%) scale(1.1)} }
        @keyframes drift2 { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(-6%,-8%) scale(1.15)} }
        @keyframes drift3 { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(-4%,6%) scale(1.08)} }
      `}</style>
    </section>
  )
}
